
import { useState, useCallback } from "react";
import { socialApi, Comment } from "@/api/social";
import { toast } from "sonner";

/**
 * Hook for handling comments functionality
 */
export const useSocialComments = (
  derivativeId: string,
  isAuthenticated: boolean,
  isAcademy: boolean,
  updateCount: (type: "comments_count", increment: boolean) => void
) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isCommentLoading, setIsCommentLoading] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);

  // Fetch comments when needed
  const fetchComments = useCallback(async () => {
    if (!derivativeId) return [];
    
    setIsCommentLoading(true);
    console.log(`Explicitly fetching comments for derivative ${derivativeId}`);
    
    try {
      const commentsData = await socialApi.getComments(derivativeId);
      
      // Ensure all comment objects have valid properties
      const sanitizedComments = commentsData.map(comment => {
        // Create a proper comment object with all required properties
        return {
          ...comment,
          // Ensure content is a string
          content: typeof comment.content === 'string' ? comment.content : 
                   comment.content ? String(comment.content) : '',
        };
      });
      
      setComments(sanitizedComments);
      setCommentsLoaded(true);
      setIsCommentLoading(false);
      return sanitizedComments;
    } catch (error) {
      console.error('Error fetching comments:', error);
      setIsCommentLoading(false);
      return [];
    }
  }, [derivativeId]);

  // Comment functions
  const addComment = useCallback(async (content: string, parentId?: string) => {
    if (!isAuthenticated) {
      return { requireAuth: true, action: "comment", data: null };
    }
    
    if (!isAcademy) {
      toast.error("Seuls les membres de l'Academy peuvent commenter");
      return { requireAuth: false, success: false, data: null };
    }
    
    if (!content.trim()) {
      toast.error("Le commentaire ne peut pas être vide");
      return { requireAuth: false, success: false, data: null };
    }
    
    setIsCommentLoading(true);
    
    try {
      const result = await socialApi.addComment(derivativeId, content, parentId);
      
      if (result.success && result.data) {
        // Get creator info and update comments list
        const { data: profile } = await socialApi.getSupabase()
          .from('profiles')
          .select('full_name, username, avatar_url, is_academy')
          .eq('id', result.data.user_id)
          .single();
        
        const commentWithCreator = {
          ...result.data,
          // Ensure content is a string
          content: typeof result.data.content === 'string' ? result.data.content : 
                  result.data.content ? String(result.data.content) : '',
          creator: {
            name: profile?.full_name || profile?.username || 'Utilisateur',
            avatar: profile?.avatar_url,
            is_academy: profile?.is_academy
          }
        };
        
        setComments(prev => [...prev, commentWithCreator]);
        
        // Update the comment count
        updateCount("comments_count", true);
        
        setIsCommentLoading(false);
        return { requireAuth: false, success: true, data: commentWithCreator };
      }
      
      setIsCommentLoading(false);
      return { requireAuth: false, success: false, data: null };
    } catch (error) {
      setIsCommentLoading(false);
      console.error("Error adding comment:", error);
      return { requireAuth: false, success: false, data: null };
    }
  }, [derivativeId, isAuthenticated, isAcademy, updateCount]);

  const updateComment = useCallback(async (commentId: string, content: string) => {
    if (!isAuthenticated || !isAcademy) {
      toast.error("Action non autorisée");
      return false;
    }
    
    if (!content.trim()) {
      toast.error("Le commentaire ne peut pas être vide");
      return false;
    }
    
    setIsCommentLoading(true);
    
    try {
      const result = await socialApi.updateComment(commentId, content);
      
      if (result.success && result.data) {
        setComments(prev => 
          prev.map(comment => 
            comment.id === commentId 
              ? { 
                  ...comment, 
                  content: typeof result.data.content === 'string' ? result.data.content : 
                           result.data.content ? String(result.data.content) : '',
                  is_edited: true, 
                  updated_at: result.data.updated_at 
                } 
              : comment
          )
        );
        
        setIsCommentLoading(false);
        return true;
      }
      
      setIsCommentLoading(false);
      return false;
    } catch (error) {
      setIsCommentLoading(false);
      console.error("Error updating comment:", error);
      return false;
    }
  }, [isAuthenticated, isAcademy]);

  const deleteComment = useCallback(async (commentId: string) => {
    if (!isAuthenticated) {
      toast.error("Action non autorisée");
      return false;
    }
    
    try {
      const result = await socialApi.deleteComment(commentId);
      
      if (result.success) {
        setComments(prev => prev.filter(comment => comment.id !== commentId));
        
        // Update the comment count
        updateCount("comments_count", false);
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Error deleting comment:", error);
      return false;
    }
  }, [isAuthenticated, updateCount]);

  return {
    comments,
    isCommentLoading,
    fetchComments,
    addComment,
    updateComment,
    deleteComment,
    commentsLoaded
  };
};
